import React, { useEffect, useRef, useState } from 'react'

import Portal from '@wiggui/ds/Portal'

import { composeClasses } from '@wiggot/utils/src/strings'
import { GoogleMapsStructuredPlace } from '../interfaces/googleMapsInterfaces'
import ResultsContainer from './ResultsContainer'
import SearchField from './SearchField'
import MyLocation from './MyLocation'
import { ESearchLevel } from '../enums'

export interface ILocation {
  id: string;
  latitude: number;
  longitude: number;
  searchAddress?: string;
  searchLevel?: ESearchLevel;
  zoneId?: number;
}

interface Props {
  /**
   * This is the myLocation of the AutocompleteResults
   * @type {boolean}
   * @default false
   */
  enableMyLocation?: boolean

  /**
   * Show the MyLocation button inline next to the input
   * @type {boolean}
   * @default false
   */
  inlineMyLocation?: boolean

  /**
   * This is the defaultSearches of the AutocompleteResults
   * @type {boolean}
   * @default false
   */
  enableDefaultSearches?: boolean

  /**
   * This is the popularSearches of the AutocompleteResults
   * @type {boolean}
   * @default false
   */
  enablePopularSearches?: boolean

  /**
   * This is the savedSearches of the AutocompleteResults
   * @type {boolean}
   * @default false
   */
  enableSavedSearches?: boolean

  /**
   * This is the zoneSearch of the AutocompleteResults
   * @type {boolean}
   * @default false
   */
  enableZoneSearches?: boolean

  /**
   * This function is called when the match is selected
   * @type {(place: GoogleMapsStructuredPlace) => void}
   * */
  onChange: (value: GoogleMapsStructuredPlace | undefined) => void

  /**
   * @description This function is called when the locations delete button is clicked
   * @type {(id:string) => void}
   */

  onDeleteLocation?: (id:string) => void
  /**
   * Indicates whether the street number must be validated.
   * @type {boolean}
   */
  validateStreetNumber?: boolean

  /**
   * Assigns default regions.
   * @type {string[]}
   */
  limitedRegions?: string[]

  /**
   * This is the types of the AutocompleteResults
   * @type {string[]}
   * @default ["geocode"]
   */
  placeTypes?: string[]

  /**
   * This is the default value of the input
   * @type {string}
   */
  defaultFieldValue?: string

  /**
   * Customizable
   */

  /**
   * @description This is the icon of the input, define the position of the icon
   * @type {string}
   * */
  alignIcon?: 'left' | 'right'

  /**
   * @description This is de className of the icon
   * @type {string}
   * */
  iconClassName?: string

  /**
   * @description This is the placeholder of the input
   * @type {string}
   * */
  placeholder?: string

  /**
   * @description This is the className of the field
   * @type {string}
   * */
  placeholderClassName?: string

  /**
   * @description This is the className of the value
   * @type {string}
   * */
  valueClassName?: string

  /**
   * @description This is the className of the results container
   * @type {string}
   * */
  resultContainerClassName?: string

  /**
   * @description This is the className of the fielText
   * @type {string}
   * */
  fieldClassName?: string

  /**
   * @description This is the className of the fielText container
   * @type {string}
   * */
  fieldContainerClassName?: string

  /**
   * @description This function is called when the input is focused or blurred
   * @type {(status: boolean) => void}
   *
   */

  /**
   * @param itemClassName - This is the className of the item.
   */
  itemClassName?: string

  /**
   * Boolean to inidicate if is necesary remark the option selected in the coincidences list
   * @type {boolean}
   */
  withRemarkSelected?: boolean

  /**
   * @description flag to inidicate if the placeholder is as placeholder or label
   * @type {boolean}
   */

  showPlaceHolderAsPlaceHolder?: boolean

  /**
   * @description locations to show in the autocomplete
   * @type {ILocation[]}
   */

  locations?: ILocation[]

  focusInput?: boolean
  onFocus?: (status: boolean) => void
  loading?: boolean
  disabled?: boolean
  enableEmptystringForResetInput?: boolean
  iconCleanSearch?: string
}

/**
 * This component is a Google Maps autocomplete type element.
 * @param enableMyLocation - This is the myLocation of the AutocompleteResults
 * @param inlineMyLocation - Show the MyLocation button next to the input
 * @param enableDefaultSearches - This is the defaultSearches of the AutocompleteResults
 * @param enablePopularSearches - This is the popularSearches of the AutocompleteResults
 * @param enableSavedSearches - This is the savedSearches of the AutocompleteResults
 * @param onChange - This function is called when the match is selected
 * @param validateStreetNumber - Indicates whether the street number must be validated.
 * @param limitedRegions - Assigns default regions.
 * @param className - This is the className of autocomplete container
 * @param defaultValue - This is the default value of the input
 * @param onChange - This function is called when the input value changes
 * @param placeTypes - This is the types of the AutocompleteResults
 * @param defaultFieldValue - This is the default value of the input
 * @param alignIcon - This is the icon of the input, define the position of the icon
 * @param iconClassName - This is de className of the icon
 * @param placeholder - This is the placeholder of the input
 * @param placeholderClassName - This is the className of the field
 * @param valueClassName - This is the className of the value
 * @param resultContainerClassName - This is the className of the results container
 * @param fieldClassName - This is the className of the fielText
 * @param fieldContainerClassName - This is the className of the fielText container
 * @param focusInput - Set the focus of the input
 * @param onFocus - This function is called when the input is focused or blurred
 * @param withRemarkSelected - boolean to remark a selected option with the searchText.
 * @paramenableEmptystringForResetInput - boolean to enable the empty string for reset the input.
 * @returns {JSX.Element}
 * */
const Autocomplete = ({
  enableMyLocation = false,
  inlineMyLocation = false,
  enableDefaultSearches = false,
  enablePopularSearches = false,
  enableSavedSearches = false,
  enableZoneSearches = false,
  onChange,
  onDeleteLocation,
  validateStreetNumber,
  limitedRegions,
  placeTypes,
  alignIcon,
  iconClassName,
  placeholder,
  placeholderClassName,
  valueClassName,
  resultContainerClassName,
  fieldClassName,
  itemClassName,
  fieldContainerClassName,
  focusInput = false,
  onFocus,
  loading,
  disabled,
  defaultFieldValue,
  enableEmptystringForResetInput,
  iconCleanSearch = '',
  withRemarkSelected,
  showPlaceHolderAsPlaceHolder = false,
  locations
}: Props): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)

  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isFocused, setIsFocused] = useState(false)
  const [searchText, setSearchText] = useState(defaultFieldValue || '')

  const handleFocus = () => {
    setIsFocused(true)
    const rect = ref.current?.getBoundingClientRect()
    setPosition({
      x: rect?.x ?? 0,
      y:
        (rect?.y ?? 0) +
        (rect?.height ?? 0) +
        document.documentElement.scrollTop
    })
  }

  /**
   * This function is called when the match is selected
   * @param place - This is the place selected
   * */
  const onSelected: (place: GoogleMapsStructuredPlace) => void = (
    place: GoogleMapsStructuredPlace
  ) => {
    setSearchText(place.fullAddress)
    setIsFocused(false)
    onChange(place)
  }

  const onClear = () => {
    setIsFocused(false)
    onChange(undefined)
  }

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (!ref.current || !resultsRef.current) {
        return
      }

      const isClickInMainElement = ref.current.contains(event.target as Node)
      const isClickInPortal = resultsRef.current.contains(event.target as Node)

      if (!isClickInMainElement && !isClickInPortal) {
        if (isFocused) {
          setIsFocused(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  })

  useEffect(() => {
    if (onFocus) {
      onFocus(isFocused)
    }
  }, [isFocused])

  useEffect(() => {
    setIsFocused(focusInput)
  }, [focusInput])

  return (
    <>
      {inlineMyLocation && enableMyLocation && (
        <MyLocation
          isLoading={loading}
          showInline={inlineMyLocation}
          onSelected={onSelected}
          limitedRegions={limitedRegions}
          isDisabled={disabled}
          isVisible
        />
      )}

      <div
        ref={ref}
        className={composeClasses('w-full', fieldContainerClassName)}
      >
        <SearchField
          disabled={disabled}
          defaultValue={defaultFieldValue}
          onChange={setSearchText}
          focusInput={isFocused}
          onFocused={(status: boolean) => {
            if (status) {
              handleFocus()
            }
          }}
          onClear={onClear}
          alignIcon={alignIcon}
          iconClassName={iconClassName}
          placeholder={placeholder}
          placeholderClassName={placeholderClassName}
          valueClassName={valueClassName}
          className={fieldClassName}
          iconCleanSearch={iconCleanSearch}
          showPlaceHolderAsPlaceHolder={showPlaceHolderAsPlaceHolder}
          enableEmptystringForResetInput={enableEmptystringForResetInput}
          locations={locations}
          onDeleteLocation={onDeleteLocation}
        />

        <Portal>
          <div
            ref={resultsRef}
            className="absolute z-20"
            style={{
              top: position.y,
              left: position.x,
              width: ref.current?.clientWidth
            }}
          >
            <ResultsContainer
              withRemarkSelected={withRemarkSelected}
              itemClassName={itemClassName}
              className={resultContainerClassName}
              isFocusedAutocomplete={isFocused}
              searchText={searchText}
              onSelected={onSelected}
              enableMyLocation={enableMyLocation && !inlineMyLocation}
              enableDefaultSearches={enableDefaultSearches}
              enablePopularSearches={enablePopularSearches}
              enableSavedSearches={enableSavedSearches}
              validateStreetNumber={validateStreetNumber}
              limitedRegions={limitedRegions}
              placeTypes={placeTypes}
              enableZoneSearches={enableZoneSearches}
              locations={locations}
              onDeleteLocation={onDeleteLocation}
            />
          </div>
        </Portal>
      </div>
    </>
  )
}

export default Autocomplete
